import { useEffect, useLayoutEffect, useRef } from "react"
import { useCurrentUser } from "./useCurrentUser"
import type { Channel } from "pusher-js"
import { holdPusherChannel, isPusherStarted } from "@/lib/pusherClient"
import { getPusherChannel } from "@/utils/pusherChannel.utils"
import {
    PubSubEventData,
    PubSubEventType,
    EventMapItem,
} from "@/schemas/pubsub.types"

export const usePubSub = <T extends PubSubEventType>(
    eventMapItems: EventMapItem<T>[]
) => {
    const eventMapItemsRef = useRef(eventMapItems)
    const orgId = useCurrentUser().session?.orgId

    useLayoutEffect(() => {
        eventMapItemsRef.current = eventMapItems
    }, [eventMapItems])

    useEffect(() => {
        if (!orgId) {
            return
        }

        const channelName = getPusherChannel(orgId)
        let boundChannel: Channel | null = null
        const bindings: Array<{
            eventType: PubSubEventType
            handler: (data: PubSubEventData) => void
        }> = []

        const handleMessage = (
            eventType: PubSubEventType,
            eventData: PubSubEventData
        ) => {
            const isEventType = <K extends PubSubEventType>(
                event: PubSubEventData,
                type: K
            ): event is Extract<PubSubEventData, { type: K }> =>
                event.type === type

            const eventMapItems = eventMapItemsRef.current ?? []

            eventMapItems.forEach(({ events, callback }) => {
                const eventsArray = Array.isArray(events) ? events : [events]
                if (
                    eventsArray.includes(eventData.type as T) &&
                    isEventType(eventData, eventData.type)
                ) {
                    callback(eventData as Extract<PubSubEventData, { type: T }>)
                }
            })
        }

        const releaseChannel = holdPusherChannel({
            channelName,
            onChannel: channel => {
                boundChannel = channel
                eventMapItemsRef.current.forEach(({ events }) => {
                    const eventsArray = Array.isArray(events)
                        ? events
                        : [events]
                    eventsArray.forEach(eventType => {
                        const handler = (data: PubSubEventData) =>
                            handleMessage(eventType, data)
                        channel.bind(eventType, handler)
                        bindings.push({ eventType, handler })
                    })
                })
            },
        })

        return () => {
            bindings.forEach(({ eventType, handler }) =>
                boundChannel?.unbind(eventType, handler)
            )
            releaseChannel()
        }
    }, [orgId])

    return {
        isConnected: isPusherStarted() && !!orgId,
        orgId,
    }
}
